import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import * as taskboardService from '../services/taskboardService';

function parseLocalDate(dateStr: string): Date {
  const parts = dateStr.split('T')[0].split('-');
  return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
}

export function SprintArchive(): React.ReactElement {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [sprints, setSprints] = useState<any[]>([]);
  const [sprintItemsMap, setSprintItemsMap] = useState<Record<string, any[]>>({});
  const [expandedSprints, setExpandedSprints] = useState<Set<string>>(new Set());
  const [loadingItems, setLoadingItems] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await taskboardService.getArchivedSprints(token);
      setSprints(res.data || []);
    } catch (err) {
      console.error('Error loading archived sprints:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  async function toggleExpand(sprintId: string) {
    if (expandedSprints.has(sprintId)) {
      setExpandedSprints(prev => {
        const next = new Set(prev);
        next.delete(sprintId);
        return next;
      });
      return;
    }

    // Load items from sprint history if not already loaded
    if (!sprintItemsMap[sprintId]) {
      setLoadingItems(prev => new Set(prev).add(sprintId));
      try {
        const historyRes = await taskboardService.getSprintHistory(sprintId, token!);
        setSprintItemsMap(prev => ({ ...prev, [sprintId]: historyRes.data || [] }));
      } catch {
        setSprintItemsMap(prev => ({ ...prev, [sprintId]: [] }));
      } finally {
        setLoadingItems(prev => {
          const next = new Set(prev);
          next.delete(sprintId);
          return next;
        });
      }
    }

    setExpandedSprints(prev => new Set(prev).add(sprintId));
  }

  function getAssigneeName(assignedTo: string | null, assignedToName: string | null): string {
    if (!assignedTo) return 'Unassigned';
    return assignedToName || 'Unknown';
  }

  function renderStoryTypeIcon(storyType: string | null) {
    if (!storyType) return <span className="text-xs text-gray-400">—</span>;
    let icon;
    switch (storyType) {
      case 'bug':
        icon = <svg className="w-4 h-4 text-red-500 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>;
        break;
      case 'task':
        icon = <svg className="w-4 h-4 text-blue-500 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>;
        break;
      case 'spike':
        icon = <svg className="w-4 h-4 text-yellow-500 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>;
        break;
      default:
        icon = null;
    }
    return (
      <span className="inline-flex items-center gap-1">
        {icon}
        <span className="text-xs text-gray-600 dark:text-gray-400 capitalize">{storyType.replace('-', ' ')}</span>
      </span>
    );
  }

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">Sprint Archive</h1>
              <p className="text-indigo-100 mt-1 text-sm">
                {sprints.length} archived sprint{sprints.length !== 1 ? 's' : ''}
              </p>
            </div>
            <button
              onClick={() => navigate('/taskboard/backlog')}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium bg-white/20 text-white hover:bg-white/30 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Backlog
            </button>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : sprints.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
            <svg className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
            </svg>
            <p className="text-gray-500 dark:text-gray-400 text-lg font-medium">No archived sprints</p>
            <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">Completed sprints can be archived from the backlog page</p>
          </div>
        ) : (
          <div className="space-y-3">
            {sprints.map(sprint => (
              <div key={sprint.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => toggleExpand(sprint.id)}
                    className="flex items-center gap-2 text-left group"
                  >
                    <svg
                      className={`w-4 h-4 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-transform duration-200 ${expandedSprints.has(sprint.id) ? 'rotate-90' : ''}`}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-gray-900 dark:text-gray-100">{sprint.name}</h3>
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                          archived
                        </span>
                        {sprint.team_name && (
                          <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                            {sprint.team_name}
                          </span>
                        )}
                        {sprint.project_name && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 font-medium">
                            {sprint.project_code || sprint.project_name}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                        {parseLocalDate(sprint.start_date).toLocaleDateString()} - {parseLocalDate(sprint.end_date).toLocaleDateString()}
                        {sprint.goal && <span className="ml-2">| {sprint.goal}</span>}
                      </p>
                    </div>
                  </button>
                </div>

                {/* Expanded sprint items */}
                {expandedSprints.has(sprint.id) && (
                  <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                    {loadingItems.has(sprint.id) ? (
                      <div className="flex items-center justify-center py-6">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
                      </div>
                    ) : (sprintItemsMap[sprint.id] || []).length === 0 ? (
                      <p className="text-xs text-gray-400 italic py-2">No items in this sprint</p>
                    ) : (
                      <div className="bg-gray-50 dark:bg-gray-900/30 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <table className="w-full">
                          <thead>
                            <tr className="bg-gray-200 dark:bg-gray-700 border-b border-gray-300 dark:border-gray-600">
                              <th className="text-left px-3 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Title</th>
                              <th className="text-center px-2 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase w-24">Type</th>
                              <th className="text-left px-2 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase w-20">Priority</th>
                              <th className="text-center px-2 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase w-14">Points</th>
                              <th className="text-left px-2 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase w-28">Assignee</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {(sprintItemsMap[sprint.id] || []).map((item: any) => (
                              <tr key={item.id} className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                                <td className="px-3 py-1.5">
                                  <button
                                    onClick={() => navigate(`/taskboard/requirement/${item.requirement_id}`)}
                                    className="text-sm text-gray-900 dark:text-gray-100 hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline text-left"
                                  >
                                    {item.requirement_code && (
                                      <span className="text-xs font-mono font-semibold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 px-2 py-0.5 rounded-full border border-indigo-200 dark:border-indigo-800 mr-2">{item.requirement_code}</span>
                                    )}
                                    {item.title}
                                  </button>
                                </td>
                                <td className="px-2 py-1.5 text-center">
                                  {renderStoryTypeIcon(item.story_type)}
                                </td>
                                <td className="px-2 py-1.5">
                                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                                    item.priority === 'critical' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                                    item.priority === 'high' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
                                    item.priority === 'medium' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                                    'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                  }`}>
                                    {item.priority}
                                  </span>
                                </td>
                                <td className="px-2 py-1.5 text-center">
                                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 text-white text-xs font-bold shadow-sm">
                                    {item.story_points_snapshot ?? item.story_points ?? '—'}
                                  </span>
                                </td>
                                <td className="px-2 py-1.5">
                                  <span className="inline-flex items-center gap-1.5">
                                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                                      {getAssigneeName(item.assigned_to, item.assigned_to_name).split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2)}
                                    </div>
                                    <span className="text-xs text-gray-700 dark:text-gray-300 truncate">{getAssigneeName(item.assigned_to, item.assigned_to_name)}</span>
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

export default SprintArchive;
